/**
 * Turning the join resolver's answer into a URL the panel can open.
 *
 * Kept as pure functions so the decisions are testable without a browser: the
 * two that matter are whether the destination is a video-platform room (which
 * is the only kind that understands a host intent) and whether the string is
 * safe to navigate to at all.
 */

/**
 * The video platform's room route. Matching on the PATH rather than the origin
 * is deliberate: the resolver returns the deployed video host, which is neither
 * this origin nor a value the panel knows, so there is nothing to compare an
 * origin against. No other destination the resolver can return has this shape —
 * a Google Meet URL is `meet.google.com/{code}`, one segment deep.
 */
const PLATFORM_MEETING_PATH = /^\/meeting\/[^/]+\/?$/;

const NAVIGABLE_PROTOCOLS = new Set(["http:", "https:"]);

const parseUrl = (value: string): URL | null => {
  try {
    return new URL(value);
  } catch {
    return null;
  }
};

/**
 * Parse the resolver's answer into an absolute URL that is safe to open.
 *
 * Its answer is data, not a trusted URL. The Google Meet fallback is typed by
 * hand into the panel's own fallback field and stored verbatim — no scheme
 * validation on either side — so it routinely arrives as
 * `meet.google.com/abc-defg-hij`. Handed to a navigation like that, a browser
 * reads it as a path on the CURRENT origin, which is how the same value 404'd
 * inside apps-video before ENG-1609.
 *
 * Returns null when the value resolves to no http(s) URL. The protocol check is
 * load-bearing rather than tidiness: this string goes straight into a
 * navigation, so a `javascript:` value in that admin-editable field would run.
 */
export const toJoinUrl = (raw: string): URL | null => {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  // Absolute if it already names a scheme; otherwise it is the bare host the
  // fallback field produces, which only ever means https.
  const url = parseUrl(trimmed) ?? parseUrl(`https://${trimmed}`);

  return url && NAVIGABLE_PROTOCOLS.has(url.protocol) ? url : null;
};

/**
 * The URL to open for a resolved join, or null if the answer was unusable.
 *
 * `asHost` is applied only to a video-platform room. Anywhere else it is
 * dropped rather than appended: Google Meet has no notion of a Focused host,
 * and a stray query parameter on someone's Meet link is noise at best.
 *
 * Set via `searchParams` rather than string concatenation so it lands before
 * the `#ml=` pre-auth fragment the resolver may have appended — glued on the
 * end it would become part of the token instead of a parameter.
 */
export const buildJoinTarget = (
  rawJoinUrl: string,
  { asHost }: { asHost: boolean }
): string | null => {
  const url = toJoinUrl(rawJoinUrl);
  if (!url) return null;

  if (asHost && PLATFORM_MEETING_PATH.test(url.pathname)) {
    url.searchParams.set("asHost", "true");
  }

  return url.href;
};

/**
 * Whether this session opens in Google Meet instead of the video platform.
 *
 * Answered from the list row rather than by resolving, because it decides
 * whether to ASK a Host how they want to join — and asking "as Host or as
 * Attendee" only to land in Google Meet would be a lie. The resolver still owns
 * the actual destination; this only owns the prompt.
 */
export const opensInGoogleMeet = (meeting: {
  useFallback?: boolean;
  fallbackMeetUrl?: string;
}): boolean => !!meeting.useFallback && !!meeting.fallbackMeetUrl;

/**
 * Whether to ask a person how they want to enter, instead of just letting them
 * in as an attendee.
 *
 * Two conditions, and both are the point of the feature:
 *
 *  - **Only a Host is asked.** Being an Admin is reach, not the right to run a
 *    session — the same line apps-video draws between `isStaff` (Hosts +
 *    Admins, who may get in) and `hasHostPrivileges` (Hosts, who may host).
 *  - **Only where the choice is real.** A session flipped to a Google Meet
 *    fallback opens in Meet, which has no notion of a Focused host. Offering
 *    the choice there and then landing in Meet would be a lie, so those go
 *    straight in.
 */
export const shouldPromptForHostIntent = ({
  isHost,
  meeting,
}: {
  isHost: boolean;
  meeting: { useFallback?: boolean; fallbackMeetUrl?: string };
}): boolean => isHost && !opensInGoogleMeet(meeting);

/**
 * The row whose Google Meet fallback governs a join of `meeting`.
 *
 * For a continuous-room segment that is the ROOM, not the segment. The server
 * swaps a segment for its day room BEFORE reading the flag
 * (`meetings_service.resolveMeetingJoinUrl`), so the segment's own value is
 * never consulted at join time — and the segment's is the only one the panel's
 * fallback editor can write, since it is keyed by GCal event and a room is
 * synthesized by the sync with no event of its own. Reading the segment would
 * label and prompt against a flag nothing acts on.
 *
 * When the room is outside the loaded page (an orphan segment), this reports no
 * fallback rather than the segment's — which is what the server concludes from
 * the room row it can see.
 */
export const resolveFallbackSubject = <
  T extends {
    id: string;
    continuousRoomId?: string;
    useFallback?: boolean;
    fallbackMeetUrl?: string;
  },
>(
  meeting: T,
  rows: T[]
): { useFallback?: boolean; fallbackMeetUrl?: string } =>
  meeting.continuousRoomId
    ? (rows.find((row) => row.id === meeting.continuousRoomId) ?? {})
    : meeting;
